import React, { useMemo } from 'react';

import { InventoryTabItemName } from '../../../../../InventoryStore';
import { useInventoryState } from '../../../../../hooks';
import { useRowConfig } from '../../../../../hooks/useRowConfig';
import { isDateAfterEndOfSale, isDatePast } from '../../../../../utils';
import type { ExperienceOpening } from '../../../../../utils/experiences';
import { LabelCell } from '../../../../Table/Cells/LabelCell/LabelCell';
import { PureFieldCell } from '../../../../Table/Cells/PureFieldCell/PureFieldCell';
import { StockInputCell } from '../../../../Table/Cells/StockInputCell/StockInputCell';
import { SwitchCell } from '../../../../Table/Cells/SwitchCell/SwitchCell';
import { TableRow } from '../../../../Table/TableRow/TableRow';
import type { ExperienceRowProps } from '../../types';
import { isEnabledByMessage } from '../utils';

const ExperienceStockRowComponent: React.FC<ExperienceRowProps> = ({
  experienceId,
  isUnlimitedStock,
  isAlwaysIncludedWithNight,
  isTimeSlot,
}) => {
  const { endOfSaleDate } = useInventoryState();
  const { inventoryExperiences, dateKeys, onDuplicate } = useRowConfig({
    entityId: experienceId,
    entityType: InventoryTabItemName.Experiences,
    fieldName: 'remainingStock',
  });

  const tooltipTitle = useMemo(() => {
    if (isAlwaysIncludedWithNight) {
      return 'Use the toggle to close availability.';
    }

    return isTimeSlot
      ? 'Total remaining timeslots for each day.'
      : 'Available stock units per day. Edit quantity or set as unlimited (∞).';
  }, [isAlwaysIncludedWithNight, isTimeSlot]);

  if (!inventoryExperiences) {
    return null;
  }

  return (
    <TableRow>
      <LabelCell
        label="Remaining stocks"
        hasInfoIcon
        tooltipTitle={tooltipTitle}
      />
      {dateKeys.map((dateKey) => {
        const fieldName = [dateKey, `${experienceId}`];
        const remainingStockFieldName = [...fieldName, 'remainingStock'];
        const isPast = isDatePast(dateKey);
        const isGrayed = isPast || isDateAfterEndOfSale(dateKey, endOfSaleDate);

        return (
          <PureFieldCell key={dateKey} name={fieldName} dateKey={dateKey}>
            {(values?: ExperienceOpening) => {
              const isDisabled = isPast || !isEnabledByMessage(values?.message);
              const isStockPublished = values?.isStockPublished ?? true;

              return isAlwaysIncludedWithNight ? (
                <SwitchCell
                  name={remainingStockFieldName}
                  label={
                    values?.remainingStock === null ||
                    values?.remainingStock === undefined
                      ? '∞'
                      : values.remainingStock
                  }
                  disabled={isDisabled}
                />
              ) : (
                <StockInputCell
                  name={remainingStockFieldName}
                  date={dateKey}
                  disabled={isDisabled}
                  grayed={isGrayed}
                  label={
                    values?.remainingStock === null ||
                    values?.remainingStock === undefined
                      ? isStockPublished
                        ? '∞'
                        : '0'
                      : values.remainingStock
                  }
                  onDuplicate={() => {
                    const value = values?.remainingStock ?? null;

                    onDuplicate(value);
                  }}
                  hasError={!isStockPublished}
                />
              );
            }}
          </PureFieldCell>
        );
      })}
    </TableRow>
  );
};

export const ExperienceStockRow = React.memo(ExperienceStockRowComponent);
